import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Sparkles, ArrowRight } from "lucide-react";

const STAGES = [
  { key: "new", label: "New", color: "border-blue-300 dark:border-blue-500/50", bar: "bg-blue-500" },
  { key: "contacted", label: "Contacted", color: "border-cyan-300 dark:border-cyan-500/50", bar: "bg-cyan-500" },
  { key: "interested", label: "Interested", color: "border-amber-300 dark:border-amber-500/50", bar: "bg-amber-500" },
  { key: "proposal_sent", label: "Proposal Sent", color: "border-violet-300 dark:border-violet-500/50", bar: "bg-violet-500" },
  { key: "won", label: "Won", color: "border-emerald-300 dark:border-emerald-500/50", bar: "bg-emerald-500" },
  { key: "lost", label: "Lost", color: "border-red-300 dark:border-red-500/50", bar: "bg-red-500" },
];

const SOURCES = ["Google","Facebook","Instagram","YouTube","Website","Walk-in","Reference","Seminar","Webinar"];

export default function Leads() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", source: "Website", interest: "", estimated_value: 0, notes: "" });

  const load = async () => { const { data } = await api.get("/leads"); setItems(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/leads", form);
    toast.success("Lead created");
    setOpen(false); setForm({ name: "", mobile: "", email: "", source: "Website", interest: "", estimated_value: 0, notes: "" });
    load();
  };

  const move = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    load();
  };

  const convert = async (id) => {
    const { data } = await api.post(`/leads/${id}/convert`);
    toast.success(`Converted to client ${data.client_code}`);
    load();
  };

  const byStage = STAGES.reduce((acc, s) => { acc[s.key] = items.filter(l => l.status === s.key); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Marketing CRM</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Lead Pipeline</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-lead-btn"><Plus className="w-4 h-4 mr-1.5" /> New lead</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">New lead</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2"><Field label="Name"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field></div>
              <Field label="Mobile"><Input value={form.mobile} onChange={e=>setForm({...form,mobile:e.target.value})} /></Field>
              <Field label="Email"><Input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></Field>
              <Field label="Source">
                <Select value={form.source} onValueChange={v=>setForm({...form,source:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Est. value (₹)"><Input type="number" value={form.estimated_value} onChange={e=>setForm({...form,estimated_value:parseFloat(e.target.value||0)})} /></Field>
              <div className="col-span-2"><Field label="Interest"><Input value={form.interest} onChange={e=>setForm({...form,interest:e.target.value})} placeholder="e.g. Vastu for new home" /></Field></div>
              <div className="col-span-2"><Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></Field></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-start" data-testid="kanban-board">
        {STAGES.map(s => (
          <div key={s.key} className="kanban-col">
            <div className={`rounded-2xl border-t-4 ${s.color} bg-white dark:bg-slate-900/60 p-4 min-h-[400px]`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className={`w-2 h-2 rounded-full ${s.bar} inline-block mr-1.5`} />
                  <span className="font-medium text-slate-700 dark:text-slate-200">{s.label}</span>
                </div>
                <span className="text-xs font-mono text-slate-500">{byStage[s.key].length}</span>
              </div>
              <div className="space-y-2">
                {byStage[s.key].map(l => (
                  <Card key={l.id} className="p-3 rounded-xl border-slate-200 dark:border-slate-800 card-lift" data-testid={`lead-${l.id}`}>
                    <div className="font-medium text-sm">{l.name}</div>
                    <div className="text-xs text-slate-500">{l.mobile || l.email}</div>
                    {l.interest && <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 italic">"{l.interest}"</div>}
                    <div className="flex items-center justify-between mt-2">
                      <Badge variant="secondary" className="text-[10px]">{l.source}</Badge>
                      {l.estimated_value > 0 && <span className="text-xs font-mono text-amber-600">₹{l.estimated_value.toLocaleString()}</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {STAGES.filter(x => x.key !== s.key && x.key !== "lost").map(next => (
                        <button key={next.key} onClick={()=>move(l.id, next.key)} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 hover:border-amber-400" data-testid={`move-${l.id}-${next.key}`}>
                          {next.label.slice(0,4)}
                        </button>
                      ))}
                      {s.key !== "won" && s.key !== "lost" && (
                        <button onClick={()=>convert(l.id)} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200" data-testid={`convert-${l.id}`}>Convert →</button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);
